import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { DateTime } from "luxon";

export interface TransitCellInput {
  shippedDate?: string | Date | null;
  receivedDate?: string | Date | null;
  eManifestDaysInTransit?: number | null;
}

export interface TransitCellProps {
  input: TransitCellInput;
}

/**
 * Abbreviated month + day, no year (e.g. "Nov 17"). The manifests list is
 * already scoped to a single ship-date year via the page header, so the year
 * is noise in the cell — it lives in the hover tooltip instead. Formatted in
 * UTC to match `getUtcDisplayDate` and the rest of the manifest date columns.
 */
function formatMonthDay(date: string | Date): string {
  const js = typeof date === "string" ? new Date(date) : date;
  const dt = DateTime.fromJSDate(js, { zone: "utc" });
  return dt.toFormat("LLL d");
}

/**
 * Single-line cell summarizing a manifest's ship → receive window, replacing
 * the standalone Ship Date / Received Date / Days in transit columns (which
 * still exist for the FILTERS popover but are hidden by default):
 *
 *   November 17th → November 20th (3)
 *
 * The parenthetical is the transit-day count. The hover tooltip spells out the
 * full dates (with year) and the transit-day number. Renders empty when there
 * is no ship or received date (DataGrid handles padding).
 */
export function TransitCell({ input }: TransitCellProps) {
  const theme = useTheme();

  const shipped = input.shippedDate ?? null;
  const received = input.receivedDate ?? null;
  const days = input.eManifestDaysInTransit ?? null;

  if (!shipped && !received) {
    return null;
  }

  const tooltip = (
    <Stack spacing={0.25}>
      <Typography variant="caption">
        Shipped: {shipped ? getUtcDisplayDate(shipped, "med") : "—"}
      </Typography>
      <Typography variant="caption">
        Received: {received ? getUtcDisplayDate(received, "med") : "—"}
      </Typography>
      <Typography variant="caption">Days in transit: {days ?? "—"}</Typography>
    </Stack>
  );

  return (
    <Tooltip title={tooltip} placement="right" enterDelay={400}>
      <Typography variant="body2" noWrap sx={{ width: "100%" }}>
        {shipped && formatMonthDay(shipped)}
        {shipped && received && (
          <Box component="span" sx={{ color: theme.palette.text.secondary, mx: 0.5 }}>
            →
          </Box>
        )}
        {received && formatMonthDay(received)}
        {days != null && (
          <Box component="span" sx={{ color: theme.palette.text.secondary }}>
            {" "}
            ({days})
          </Box>
        )}
      </Typography>
    </Tooltip>
  );
}
